import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { AffiliateHeader, AffiliateFooter } from '../../components/affiliate/AffiliateChrome';
import { affiliateApi, buildAffiliatePath } from '../../lib/affiliate';

const TRAFFIC_SOURCES = [
  'Instagram', 'TikTok', 'YouTube', 'Facebook', 'Twitter / X', 'Blog / Website',
  'Email list', 'Podcast', 'Gym / In-person', 'Paid ads', 'Other',
];
const MONTHLY_TRAFFIC = [
  '< 1,000', '1,000 - 10,000', '10,000 - 50,000', '50,000 - 250,000', '250,000+',
];
const PAYOUT_METHODS = ['PayPal', 'Bank transfer (ACH)', 'Wire transfer', 'Venmo', 'Other'];

const slugify = (s) =>
  (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30);

const initial = {
  first_name: '', last_name: '', email: '', password: '',
  phone: '', subdomain: '', website_social: '', country: 'US',
  traffic_source: '', monthly_traffic: '', payout_method: '', payout_details: '',
  // Auto-accepted on the server side; the user-facing disclosure block was
  // removed per product request, so we always send `true` here.
  accept_descriptor: true,
};

const AffiliateApplyPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [success, setSuccess] = useState(null);
  const [touchedSub, setTouchedSub] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onFirstNameChange = (e) => {
    const v = e.target.value;
    update('first_name', v);
    if (!touchedSub) {
      const last = form.last_name;
      const auto = slugify(`${v}${last ? last : ''}`);
      update('subdomain', auto);
    }
  };
  const onLastNameChange = (e) => {
    const v = e.target.value;
    update('last_name', v);
    if (!touchedSub) {
      const first = form.first_name;
      const auto = slugify(`${first}${v}`);
      update('subdomain', auto);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if ((form.password || '').length < 8) {
      setServerError('Password must be at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await affiliateApi('/affiliates/signup', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          subdomain: (form.subdomain || '').trim().toLowerCase(),
          accept_descriptor: true,
        }),
      });
      // Defensive: backend should return { ok, affiliate: {...} } but in some
      // proxy/edge configurations the body can be empty or malformed. Fall back
      // to the form values so the success screen still renders correctly.
      const aff = (data && data.affiliate) ? data.affiliate : {
        subdomain: (form.subdomain || '').trim().toLowerCase(),
      };
      setSuccess(aff);
    } catch (err) {
      setServerError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white">
        <AffiliateHeader />
        <section className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
          <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-emerald-400" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-3">Application received</h1>
          <p className="text-neutral-300 mb-2">
            Thanks for applying to the Axon Muscle Affiliate Program.
          </p>
          <p className="text-neutral-400 text-sm mb-8">
            We&apos;ll review your application and email you when it&apos;s approved. Once approved, your link will be live at:
            <br />
            <span className="font-mono text-axon-400">https://{success.subdomain}.axonmuscle.com</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to={buildAffiliatePath('/login')}
              className="px-6 py-3 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 text-white font-semibold uppercase tracking-wider text-sm shadow-button hover:shadow-glow-cyan transition-all inline-flex items-center justify-center gap-2"
            >
              Sign in to your account
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to={buildAffiliatePath('/')}
              className="px-6 py-3 rounded-xl border border-neutral-700 hover:border-axon-500 text-neutral-200 font-semibold uppercase tracking-wider text-sm transition-colors"
            >
              Back to overview
            </Link>
          </div>
        </section>
        <AffiliateFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      <AffiliateHeader />
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-2">Apply to the Affiliate Program</h1>
        <p className="text-neutral-400 mb-8">
          Tell us a bit about yourself and how you&apos;ll promote Axon Muscle. Most applications are reviewed within 48 hours.
        </p>

        {serverError && (
          <div className="mb-5 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{serverError}</span>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6 bg-neutral-900/60 border border-neutral-800 rounded-2xl p-6 md:p-8" data-testid="affiliate-apply-form">
          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First Name *">
              <input required value={form.first_name} onChange={onFirstNameChange} className={inp} data-testid="first-name" />
            </Field>
            <Field label="Last Name *">
              <input required value={form.last_name} onChange={onLastNameChange} className={inp} data-testid="last-name" />
            </Field>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Email *">
              <input required type="email" value={form.email} onChange={(e) => update('email', e.target.value)} className={inp} data-testid="email" />
            </Field>
            <Field label="Phone">
              <input type="tel" value={form.phone} onChange={(e) => update('phone', e.target.value)} className={inp} data-testid="phone" />
            </Field>
          </div>

          {/* Password */}
          <Field label="Password * (min 8 characters — used to sign in to your dashboard)">
            <input required type="password" minLength={8} value={form.password} onChange={(e) => update('password', e.target.value)} className={inp} data-testid="password" />
          </Field>

          {/* Subdomain */}
          <Field label="Desired Affiliate Name *">
            <div className="flex items-stretch rounded-xl overflow-hidden border border-neutral-700 focus-within:border-axon-500 transition-colors">
              <input
                required
                placeholder="johndoe"
                value={form.subdomain}
                onChange={(e) => { update('subdomain', slugify(e.target.value)); setTouchedSub(true); }}
                className="flex-1 bg-neutral-900 text-white px-4 py-3 outline-none"
                data-testid="subdomain"
              />
              <span className="flex items-center px-4 bg-neutral-800 text-neutral-400 text-sm border-l border-neutral-700">.axonmuscle.com</span>
            </div>
            <p className="text-xs text-neutral-500 mt-1">3–30 chars. Lowercase letters, numbers, and hyphens. We&apos;ll reserve this if approved.</p>
          </Field>

          {/* Web / social */}
          <Field label="Website / Social Media">
            <input placeholder="https://instagram.com/yourhandle" value={form.website_social} onChange={(e) => update('website_social', e.target.value)} className={inp} data-testid="website-social" />
          </Field>

          {/* Country */}
          <Field label="Country *">
            <input required value={form.country} onChange={(e) => update('country', e.target.value)} className={inp} data-testid="country" />
          </Field>

          {/* Traffic */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Traffic Source *">
              <select required value={form.traffic_source} onChange={(e) => update('traffic_source', e.target.value)} className={inp} data-testid="traffic-source">
                <option value="">Select…</option>
                {TRAFFIC_SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Monthly Traffic (optional)">
              <select value={form.monthly_traffic} onChange={(e) => update('monthly_traffic', e.target.value)} className={inp} data-testid="monthly-traffic">
                <option value="">Select…</option>
                {MONTHLY_TRAFFIC.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
          </div>

          {/* Payout */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Payout Method *">
              <select required value={form.payout_method} onChange={(e) => update('payout_method', e.target.value)} className={inp} data-testid="payout-method">
                <option value="">Select…</option>
                {PAYOUT_METHODS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Payout Details">
              <input placeholder="PayPal email, bank info, etc." value={form.payout_details} onChange={(e) => update('payout_details', e.target.value)} className={inp} data-testid="payout-details" />
            </Field>
          </div>

          {/* Disclosure block removed per product request — `accept_descriptor`
              is auto-set to true in the submit payload. */}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl bg-gradient-to-r from-axon-600 to-axon-500 hover:from-axon-500 hover:to-axon-400 disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm shadow-button hover:shadow-glow-cyan transition-all"
            data-testid="submit-application"
          >
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Submit application <ArrowRight className="w-5 h-5" /></>}
          </button>
          <p className="text-center text-xs text-neutral-500">
            Already an affiliate? <Link to={buildAffiliatePath('/login')} className="text-axon-400 hover:text-axon-300">Sign in</Link>
          </p>
        </form>
      </section>
      <AffiliateFooter />
    </div>
  );
};

const inp = 'w-full bg-neutral-900 text-white px-4 py-3 rounded-xl border border-neutral-700 focus:border-axon-500 outline-none transition-colors';

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block text-xs font-semibold uppercase tracking-wider text-neutral-400 mb-1.5">{label}</span>
    {children}
  </label>
);

export default AffiliateApplyPage;
